import React, { useState, useMemo, useCallback } from 'react';
import { Form, Button } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import Icon from '../common/Icons';
import { useForm } from '../../hooks/useForm';
import { useAnimation, useFocusAnimation } from '../../hooks/useAnimation';
import { getErrorMessage, getErrorType, validationRules } from '../../utils/errorHandler';
import ErrorNotification from '../common/ErrorNotification';
import { LOGIN_CONSTANTS } from '../../constants/login';
import { PLATFORM_BRANDING } from '../../constants/branding';
import './LoginForm.css';

const B = PLATFORM_BRANDING;

function BrandTitle({ variant = 'brand', as: Tag = 'h1' }) {
  const { PRIMARY, ACCENT } = B.NAME_PARTS;
  return (
    <Tag className={`login-brand-title login-brand-title--${variant}`}>
      <span className="login-brand-title__plate">
        <span className="login-brand-title__primary">{PRIMARY}</span>
        <span className="login-brand-title__accent">{ACCENT}</span>
      </span>
    </Tag>
  );
}

function LoginLogoHero({ variant = 'brand', visible = true }) {
  return (
    <div
      className={`login-logo-hero login-logo-hero--${variant} ${visible ? 'is-visible' : ''}`}
      aria-hidden={variant === 'brand' ? undefined : false}
    >
      <div className="login-logo-hero__frame">
        <img
          src={B.LOGO_SRC}
          alt={variant === 'mobile' ? B.LOGO_ALT : ''}
          className="login-logo-hero__img"
          width={variant === 'brand' ? 112 : 80}
          height={variant === 'brand' ? 112 : 80}
          decoding="async"
        />
      </div>
    </div>
  );
}

const LoginForm = () => {
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loginPhase, setLoginPhase] = useState('idle');
  const [errorNotification, setErrorNotification] = useState({
    show: false,
    message: '',
    type: 'error'
  });

  const { isVisible } = useAnimation(LOGIN_CONSTANTS.ANIMATION.DELAY);
  const { handleFocus, handleBlur, isFocused } = useFocusAnimation();

  const initialValues = useMemo(
    () => ({
      identifier: '',
      password: ''
    }),
    []
  );

  const formValidationRules = useMemo(
    () => ({
      identifier: validationRules.loginIdentifier,
      password: validationRules.loginPassword
    }),
    []
  );

  const {
    values: formData,
    errors,
    isSubmitting,
    handleChange,
    handleBlur: handleFormBlur,
    handleFocus: handleFormFocus,
    validateAll,
    clearErrors,
    setIsSubmitting
  } = useForm(initialValues, formValidationRules);

  const handleFieldChange = useCallback(
    (e) => {
      handleChange(e);
      handleFormFocus(e.target.name);
    },
    [handleChange, handleFormFocus]
  );

  const handleFieldBlur = useCallback(
    (e) => {
      handleFormBlur(e);
      handleBlur();
    },
    [handleFormBlur, handleBlur]
  );

  const handleFieldFocus = useCallback(
    (fieldName) => {
      handleFormFocus(fieldName);
      handleFocus(fieldName);
    },
    [handleFormFocus, handleFocus]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginPhase('loading');
    setIsSubmitting(true);
    clearErrors();
    setErrorNotification({ show: false, message: '', type: 'error' });

    if (!validateAll()) {
      setLoginPhase('idle');
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await login(formData.identifier, formData.password);

      if (!result.success) {
        setLoginPhase('idle');
        setErrorNotification({
          show: true,
          message: getErrorMessage({ message: result.message }),
          type: getErrorType({ message: result.message })
        });
      } else {
        setLoginPhase('success');
        await new Promise((resolve) => setTimeout(resolve, 650));
      }
    } catch (error) {
      setLoginPhase('idle');
      setErrorNotification({
        show: true,
        message: getErrorMessage(error),
        type: getErrorType(error)
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleCloseError = useCallback(() => {
    setErrorNotification({ show: false, message: '', type: 'error' });
  }, []);

  return (
    <div className="login-page">
      <div className="login-shell">
        <aside className="login-brand" aria-label="Présentation de la plateforme">
          <div className="login-brand__glow" aria-hidden />
          <div className="login-brand__content">
            <LoginLogoHero variant="brand" />
            <p className="login-brand__eyebrow">{B.TAGLINE}</p>
            <BrandTitle variant="brand" />
            <p className="login-brand__desc">{B.DESCRIPTION}</p>
            <ul className="login-brand__highlights">
              {B.HIGHLIGHTS.map((item) => (
                <li key={item}>
                  <Icon name="checkCircle" size={16} aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <main className={`login-panel ${isVisible ? 'is-visible' : ''}`}>
          <div className="login-panel__mobile-hero">
            <LoginLogoHero variant="mobile" visible={isVisible} />
            <div className="login-panel__mobile-text">
              <BrandTitle variant="mobile" as="p" />
              <span>{B.TAGLINE}</span>
            </div>
          </div>

          <div className="login-card">
            <header className="login-card__header">
              <h2 className="login-card__title">{B.LOGIN.TITLE}</h2>
              <p className="login-card__subtitle">{B.LOGIN.SUBTITLE}</p>
            </header>

            <Form onSubmit={handleSubmit} className="login-form" noValidate>
              <ErrorNotification
                show={errorNotification.show}
                message={errorNotification.message}
                type={errorNotification.type}
                onClose={handleCloseError}
              />

              <Form.Group className="login-field">
                <Form.Label className="login-field__label" htmlFor="login-identifier">
                  <Icon name="user" size={16} aria-hidden />
                  E-mail ou nom d&apos;utilisateur
                </Form.Label>
                <Form.Control
                  id="login-identifier"
                  type="text"
                  name="identifier"
                  autoComplete="username"
                  spellCheck="false"
                  value={formData.identifier}
                  onChange={handleFieldChange}
                  onFocus={() => handleFieldFocus('identifier')}
                  onBlur={handleFieldBlur}
                  placeholder={LOGIN_CONSTANTS.PLACEHOLDERS.IDENTIFIER}
                  className={`login-field__input ${errors.identifier ? 'is-invalid' : ''} ${isFocused('identifier') ? 'is-focused' : ''}`}
                  required
                />
                {errors.identifier ? (
                  <div className="login-field__error" role="alert">
                    <Icon name="exclamationCircle" size={14} aria-hidden />
                    {errors.identifier}
                  </div>
                ) : null}
              </Form.Group>

              <Form.Group className="login-field">
                <Form.Label className="login-field__label" htmlFor="login-password">
                  <Icon name="lock" size={16} aria-hidden />
                  Mot de passe
                </Form.Label>
                <div className="login-field__password-wrap">
                  <Form.Control
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleFieldChange}
                    onFocus={() => handleFieldFocus('password')}
                    onBlur={handleFieldBlur}
                    placeholder={LOGIN_CONSTANTS.PLACEHOLDERS.PASSWORD}
                    className={`login-field__input ${errors.password ? 'is-invalid' : ''} ${isFocused('password') ? 'is-focused' : ''}`}
                    required
                  />
                  <button
                    type="button"
                    className="login-field__toggle"
                    onClick={togglePasswordVisibility}
                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  >
                    <Icon name={showPassword ? 'eyeSlash' : 'eye'} size={16} />
                  </button>
                </div>
                {errors.password ? (
                  <div className="login-field__error" role="alert">
                    <Icon name="exclamationCircle" size={14} aria-hidden />
                    {errors.password}
                  </div>
                ) : null}
              </Form.Group>

              <Button
                type="submit"
                className={`login-submit morph-${loginPhase}`}
                disabled={isSubmitting}
              >
                {loginPhase === 'success' ? (
                  <span className="login-submit__success" aria-live="polite">
                    <Icon name="checkCircle" size={18} aria-hidden />
                    Connecté
                  </span>
                ) : isSubmitting ? (
                  <span className="login-submit__loading" aria-live="polite">
                    <span className="login-submit__spin" aria-hidden />
                    Connexion en cours…
                  </span>
                ) : (
                  <>
                    <Icon name="signIn" size={16} aria-hidden />
                    Se connecter
                  </>
                )}
              </Button>
            </Form>

            <footer className="login-card__footer">
              <p className="login-card__secure">
                <Icon name="shield" size={14} aria-hidden />
                {B.LOGIN.SECURE_HINT}
              </p>
              <p className="login-card__legal">{B.LOGIN.FOOTER}</p>
              <small className="login-card__copy">{B.copyright()}</small>
            </footer>
          </div>
        </main>
      </div>
    </div>
  );
};

export default LoginForm;
